/* eslint-disable no-param-reassign */
import { Injectable } from '@angular/core';
import { HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, of, Subject } from 'rxjs';
import { catchError, tap, switchMap } from 'rxjs/operators';
import { Store, select } from '@ngrx/store';
import { Router } from '@angular/router';

import * as rootReducer from '../store';
import * as fromAuthSelector from '../store/selectors/auth.selectors';
import { AuthService, TOKENS } from '../services/auth.service';
import { API } from '../../shared/constants/app-service-constants';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  refreshTokenInProgress = false;
  authToken = '';
  tokenRefreshedSource = new Subject<void>();
  tokenRefreshed$ = this.tokenRefreshedSource.asObservable();

  constructor(
    private store: Store<rootReducer.State>,
    private authService: AuthService,
    private router: Router,
  ) {
    this.store.pipe(select(fromAuthSelector.getAuthAccessToken)).subscribe((response: any) => {
      this.authToken = response || localStorage.getItem(TOKENS.bearerToken);
    });
  }

  refreshToken() {
    if (this.refreshTokenInProgress) {
      return new Observable((observer: any) => {
        this.tokenRefreshed$.subscribe(() => {
          observer.next(null);
          observer.complete();
        });
      });
    }
    this.refreshTokenInProgress = true;

    return this.authService.getRefreshToken().pipe(
      tap((resp: any) => {
        this.authToken = resp;
        this.refreshTokenInProgress = false;
        this.tokenRefreshedSource.next();
      }),
      catchError((err: any) => {
        this.authService.appLocalLogout();
        this.refreshTokenInProgress = false;
        this.tokenRefreshedSource.next();
        throw err;
      }),
    );
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    // eslint-disable-next-line no-param-reassign
    request = this.setRequestToken(request, this.authToken);

    return next.handle(request).pipe(
      catchError((err) => {
        if (this.isTokenRejected(err)) {
          this.authService.appLocalLogout();
        } else if (this.isPermissionExeption(err) && !this.isWrongPasswordExeption(err)) {
          return this.refreshToken().pipe(
            switchMap(() => {
              if (this.authToken) {
                request = this.setRequestToken(request, this.authToken);
                return next.handle(request);
              }
              return of();
            }),
          );
        }

        throw err;
      }),
    );
  }

  setRequestToken(request: HttpRequest<any>, token: string) {
    if (token && !request.url.includes('auth/getAccessToken')) {
      request = request.clone({
        setHeaders: {
          Authorization: token,
        },
      });
    }
    return request;
  }

  private isTokenRejected(err): boolean {
    return this.isPermissionExeption(err) && err?.url.includes(API.GET_ACCESS_TOKEN);
  }

  private isWrongPasswordExeption(err) {
    return err?.error?.failedCount || err?.url?.includes('/auth');
  }

  private isPermissionExeption(err) {
    return err?.status === 401;
  }
}
